import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { loadStripe } from "@stripe/stripe-js"
import { settlePaymentIntent, stripeLocale } from "../../utils/stripe-intent.util"
import { keepStripeFramesOutOfTurboCache } from "../../utils/stripe-turbo-cache.util"
import { trackDiagnostic } from "../../utils/diagnostic-event.util"
import { getCsrfToken } from "../../utils/webauthn"

const LUCANTO_APPEARANCE_LIGHT = {
  theme: "stripe",
  variables: {
    colorPrimary: "#432277",
    colorBackground: "#ffffff",
    colorText: "#3D3D46",
    colorDanger: "#B80546",
    fontFamily: "'InterVariable', system-ui, -apple-system, sans-serif",
    fontSizeBase: "14px",
    borderRadius: "12px",
    spacingUnit: "4px"
  },
  rules: {
    ".Input": {
      borderColor: "#E2E6ED",
      boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
      padding: "0.45rem 1.2rem"
    },
    ".Input:focus": {
      borderColor: "#432277",
      boxShadow: "0 0 0 0.25rem rgba(67, 34, 119, 0.25)"
    },
    ".Label": {
      color: "#706D79",
      fontWeight: "500",
      fontSize: "13px"
    }
  }
}

// Mirror of LUCANTO_APPEARANCE_LIGHT with brand and surface tokens swapped to
// the dark-theme values from app/assets/stylesheets/_content.scss. Keeps the
// rule set minimal — .Input / .Input:focus / .Label only — because every
// extra rule I added beyond these (.Block, .Tab, .Action, .Button) targeted
// class names that Stripe doesn't actually apply in this Element variant,
// so they were dead code with no visible effect.
const LUCANTO_APPEARANCE_DARK = {
  theme: "flat",
  variables: {
    colorPrimary: "#B69AFF",
    colorBackground: "#202430",
    colorText: "#F4F4F7",
    colorTextSecondary: "#AAABB8",
    colorTextPlaceholder: "#6F7181",
    colorDanger: "#F87171",
    fontFamily: "'InterVariable', system-ui, -apple-system, sans-serif",
    fontSizeBase: "14px",
    borderRadius: "12px",
    spacingUnit: "4px"
  },
  rules: {
    ".Input": {
      backgroundColor: "#2A2F3D",
      borderColor: "rgba(255, 255, 255, 0.08)",
      boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.02), 0px 1px 2px rgba(0, 0, 0, 0.28)",
      padding: "0.45rem 1.2rem"
    },
    ".Input:focus": {
      borderColor: "#B69AFF",
      boxShadow: "0 0 0 0.3rem rgba(182, 154, 255, 0.12)"
    },
    ".Label": {
      color: "#B8B9C6",
      fontWeight: "500",
      fontSize: "13px"
    }
  }
}

function isDarkTheme() {
  const root = document.documentElement
  return root.dataset.theme === "dark" || root.getAttribute("data-bs-theme") === "dark"
}

function lucantoAppearance() {
  return isDarkTheme() ? LUCANTO_APPEARANCE_DARK : LUCANTO_APPEARANCE_LIGHT
}

// Payment Elements whose iframe finished loading — tracked per Element, not
// per controller, because a reconnect can mount a second one while the first
// is still loading.
const readyPaymentElements = new WeakSet()
const failedPaymentElements = new WeakSet()

function withParam(url, key, value) {
  const separator = url.includes("?") ? "&" : "?"
  return `${url}${separator}${key}=${encodeURIComponent(value)}`
}

export default class extends Controller {
  #connectionGeneration = 0

  static targets = ["paymentElement", "errors", "paymentMethodId", "submitButton", "spinner", "attemptKey"]
  static values = {
    publishableKey: String,
    clientSecret: String,
    returnUrl: String,
    mode: { type: String, default: "setup" },
    deferredAmount: Number,
    deferredCurrency: String,
    genericError: { type: String, default: "Something went wrong. Please try again." },
    networkError: { type: String, default: "Network error. Please try again." }
  }

  async connect() {
    if (!this.publishableKeyValue) return
    const generation = ++this.#connectionGeneration
    this.submitting = false

    // One key per checkout attempt: identifies this attempt to the backend for
    // idempotency, and caches the PaymentMethod so a retry after a backend
    // error reuses it instead of minting a new one. Reset whenever the user's
    // commercial intent changes (card details edited, coupon applied/removed).
    this.reusablePaymentMethodId = null
    this.regenerateAttemptKey()

    keepStripeFramesOutOfTurboCache()
    const stripe = await loadStripe(this.publishableKeyValue, { locale: stripeLocale() })

    // Turbo may have navigated away while Stripe was loading
    if (generation !== this.#connectionGeneration || !this.element.isConnected) return
    this.stripe = stripe

    // Listen for coupon events from the coupon controller (dispatched via Stimulus, bubble through DOM).
    // Registered for every mode — including saved_card, which mounts no Payment
    // Element below but still needs the attempt key to change when commercial
    // intent (the applied promotion_code) changes underneath it.
    this.handleCouponApplied = (event) => {
      // Commercial intent changed: this is a new checkout attempt.
      this.regenerateAttemptKey()
      if (this.modeValue === "payment" && this.elements && event.detail?.amount != null) {
        this.elements.update({ amount: event.detail.amount })
      }
    }
    this.handleCouponRemoved = () => {
      this.regenerateAttemptKey()
      if (this.modeValue === "payment" && this.elements) {
        this.elements.update({ amount: this.deferredAmountValue })
      }
    }
    document.addEventListener("billing--coupon:applied", this.handleCouponApplied)
    document.addEventListener("billing--coupon:removed", this.handleCouponRemoved)

    // No Element to mount, but Stripe.js is needed for handleNextAction (3DS).
    if (this.modeValue === "saved_card") return

    if (this.modeValue === "payment") {
      // Non-trial: deferred Payment Element (no intent yet).
      // The PaymentIntent comes from subscription creation after form submit.
      this.elements = this.stripe.elements({
        mode: "payment",
        amount: this.deferredAmountValue,
        currency: this.deferredCurrencyValue || "eur",
        setupFutureUsage: "off_session",
        paymentMethodCreation: "manual",
        appearance: lucantoAppearance()
      })
    } else {
      // SetupIntent-backed Payment Element (mode: "setup" for trial, "update" for payment method change).
      if (!this.clientSecretValue) return
      this.elements = this.stripe.elements({
        clientSecret: this.clientSecretValue,
        appearance: lucantoAppearance()
      })
    }

    // Disable Link in the Payment Element. Link's saved-card view and
    // identity-confirmation popups are rendered by Stripe Link's own iframe
    // (LinkBrandedRoot) and are NOT controlled by the appearance API, so
    // they always render in light mode regardless of the page theme. With
    // Link disabled, users go through the standard card / wallet tabs that
    // we DO control. Re-enable once Stripe ships Link dark mode upstream.
    const paymentElement = this.elements.create("payment", {
      layout: "tabs",
      wallets: { link: "never" }
    })
    this.paymentElement = paymentElement
    paymentElement.mount(this.paymentElementTarget)

    // Stripe.js also puts iframes straight into <body> (its controller frames
    // and the Payment Element's "loader-ui"), so a Turbo snapshot of this page
    // clones them. Restoring that snapshot (Back) reloads the clones and
    // Stripe.js rejects them: "loaderUi Element didn't mount normally". A page
    // hosting a Payment Element is therefore never cached: Back fetches it
    // afresh (a new SetupIntent in setup/update mode, an unsubmitted form
    // emptied). Set at runtime because the card-update form also renders inline
    // into the billing page's frame, where a <meta> in its response would not
    // reach the page's <head>; billing#show then stays uncached until the next
    // visit restores the layout's meta.
    Turbo.cache.exemptPageFromCache()

    paymentElement.on("ready", () => readyPaymentElements.add(paymentElement))
    paymentElement.on("loaderror", (event) => this.handleLoadError(paymentElement, event))

    paymentElement.on("change", (event) => {
      // A superseded Element (reconnect mid-load) no longer speaks for the form.
      if (paymentElement !== this.paymentElement) return
      if (this.hasErrorsTarget) {
        this.errorsTarget.textContent = event.error ? event.error.message : ""
      }
      // Card content changed: any cached PaymentMethod is stale, and this is
      // effectively a new attempt.
      this.reusablePaymentMethodId = null
      this.regenerateAttemptKey()
    })
  }

  disconnect() {
    this.#connectionGeneration += 1
    if (this.handleCouponApplied) document.removeEventListener("billing--coupon:applied", this.handleCouponApplied)
    if (this.handleCouponRemoved) document.removeEventListener("billing--coupon:removed", this.handleCouponRemoved)
    this.handleCouponApplied = null
    this.handleCouponRemoved = null
    const paymentElement = this.paymentElement
    this.paymentElement = null
    this.elements = null
    if (!paymentElement) return

    // An Element torn out of the page while its iframe is still loading fails
    // that load, and Stripe.js then destroys it itself — destroying it first
    // makes that self-destruct throw "This Element has already been destroyed"
    // from inside Stripe's message handler. Leave a loading Element to Stripe,
    // and destroy it only should it become ready after all. One that already
    // failed has been destroyed by Stripe.js and is left alone.
    if (readyPaymentElements.has(paymentElement)) {
      paymentElement.destroy()
    } else if (!failedPaymentElements.has(paymentElement)) {
      paymentElement.on("ready", () => paymentElement.destroy())
    }
  }

  // Stripe.js reports a load failure nobody listens for as an unhandled error.
  // The live Element failing (bad client secret, Stripe outage, a blocker)
  // leaves the form unusable: say so and keep it reported. One already torn out
  // of the page (the user left mid-load) failing is expected and harmless.
  // Stripe's own message is developer-facing English ("No such setupintent…"),
  // so the form shows the localized generic error and the detail goes to the log.
  // The console is not collected by Better Stack, so the dead end is also sent
  // as a diagnostic event — Stripe's error type only, never its message text.
  handleLoadError(paymentElement, event) {
    failedPaymentElements.add(paymentElement)
    if (paymentElement !== this.paymentElement) {
      console.warn("[checkout] payment element left mid-load", event?.error)
      return
    }
    console.error("[checkout] payment element loaderror", event?.error)
    trackDiagnostic("checkout.payment_element_load_failed", {
      mode: this.modeValue,
      error_type: event?.error?.type ?? "unknown"
    })
    this.showError(this.genericErrorValue)
  }

  async submit(event) {
    event.preventDefault()
    this.setLoading(true)
    this.clearErrors()

    try {
      if (this.modeValue === "saved_card") {
        await this.submitSavedCardMode()
      } else if (this.modeValue === "payment") {
        await this.submitPaymentMode()
      } else {
        await this.submitSetupMode()
      }
    } catch (error) {
      console.error("[checkout]", error)
      this.showError(error?.message || this.genericErrorValue)
    } finally {
      if (!this.submitting) this.setLoading(false)
    }
  }

  // Non-trial: validate card → create PaymentMethod → send to backend → charge immediately.
  // A retry of the same attempt (e.g. after a backend error) reuses the cached
  // PaymentMethod instead of minting a new one for the same card details.
  async submitPaymentMode() {
    let paymentMethodId = this.reusablePaymentMethodId
    if (!paymentMethodId) {
      // Step 1: Validate the Payment Element
      const { error: submitError } = await this.elements.submit()
      if (submitError) {
        console.error("[checkout] elements.submit", submitError)
        this.showError(submitError.message)
        return
      }

      // Step 2: Create a PaymentMethod from the collected card details
      const { error: pmError, paymentMethod } = await this.stripe.createPaymentMethod({ elements: this.elements })
      if (pmError) {
        console.error("[checkout] createPaymentMethod", pmError)
        this.showError(pmError.message)
        return
      }

      paymentMethodId = paymentMethod.id
      this.reusablePaymentMethodId = paymentMethodId
    }

    // Step 3: POST to backend with payment_method_id
    await this.postAndConfirm(paymentMethodId)
  }

  // Saved card: post directly; backend charges off-session, 3DS if challenged.
  async submitSavedCardMode() {
    await this.postAndConfirm()
  }

  async postAndConfirm(paymentMethodId = null) {
    const form = this.element.closest("form")
    const formData = new FormData(form)
    if (paymentMethodId) formData.set("payment_method_id", paymentMethodId)
    formData.set("checkout_attempt_key", this.attemptKey)

    const response = await fetch(form.action, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken()
      },
      body: formData
    }).catch((err) => {
      console.error("[checkout] fetch", err)
      return null
    })

    if (!response) {
      this.showError(this.networkErrorValue)
      return
    }

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      console.error("[checkout] backend error", response.status, data)
      this.showError(data.error || this.genericErrorValue)
      return
    }

    // The first invoice's PaymentIntent, when the backend hands one back: confirm
    // it, run its 3DS challenge, and only then leave the page.
    // Use status from backend when available to avoid an extra Stripe API round-trip.
    // Wallets like Google Pay complete instantly, so the intent may already be succeeded.
    if (data.client_secret) {
      const status =
        data.payment_intent_status ||
        (await this.stripe.retrievePaymentIntent(data.client_secret)).paymentIntent?.status

      if (!(await this.settlePaymentIntent(data.client_secret, status, paymentMethodId))) return
    }

    this.submitting = true
    window.location.href = this.pendingCheckoutRedirectUrl(data.pending_subscription_id)
  }

  // Resolves true once the intent is paid (or processing) and false after an
  // error has been shown. See utils/stripe-intent.util.js for the state table.
  async settlePaymentIntent(clientSecret, initialStatus, paymentMethodId) {
    const result = await settlePaymentIntent(this.stripe, {
      clientSecret,
      status: initialStatus,
      paymentMethodId,
      returnUrl: this.confirmReturnUrl()
    })
    if (result.ok) return true

    console.error("[checkout] payment intent", result.reason || result.error)
    this.showError(result.error || this.genericErrorValue)
    return false
  }

  // A cancel+replace upgrade (Subscriptions::UpgradeService#upgrade_via_replace)
  // creates the target Stripe subscription synchronously but only finalizes it
  // (cancels the source, converges the local row) once its first invoice is
  // paid — see Subscriptions::ReplacementFinalizer. Carrying the pending id back
  // to billing#show lets it finalize instantly instead of waiting on the webhook.
  pendingCheckoutRedirectUrl(pendingSubscriptionId) {
    return pendingSubscriptionId
      ? `${this.billingUrl()}?pending_checkout=${encodeURIComponent(pendingSubscriptionId)}`
      : this.billingUrl()
  }

  // Trial + card: confirm SetupIntent to save card for future charges
  async submitSetupMode() {
    const { error } = await this.stripe.confirmSetup({
      elements: this.elements,
      confirmParams: { return_url: this.confirmReturnUrl() },
      redirect: "if_required"
    })

    if (error) {
      console.error("[checkout] confirmSetup", error)
      this.showError(error.message)
      return
    }

    const { setupIntent } = await this.stripe.retrieveSetupIntent(this.clientSecretValue)
    if (setupIntent?.payment_method) {
      this.submitting = true
      this.paymentMethodIdTarget.value = setupIntent.payment_method
      if (this.hasAttemptKeyTarget) this.attemptKeyTarget.value = this.attemptKey
      this.element.closest("form").requestSubmit()
    } else {
      console.error("[checkout] setupIntent missing payment_method", setupIntent)
      this.showError(this.genericErrorValue)
    }
  }

  regenerateAttemptKey() {
    this.attemptKey = crypto.randomUUID()
    if (this.hasAttemptKeyTarget) this.attemptKeyTarget.value = this.attemptKey
  }

  // Where a redirect-based confirmation (a bank's 3DS page, PayPal, iDEAL)
  // lands back: billing#confirm, carrying the applied promotion code, a
  // scheduled downgrade's keep counts and this attempt's key so the
  // server-side completion sees the same checkout.
  confirmReturnUrl() {
    let returnUrl = this.returnUrlValue
    const promoField = document.querySelector('input[name="promotion_code"]')
    if (promoField?.value) returnUrl = withParam(returnUrl, "promotion_code", promoField.value)
    for (const [name, value] of this.keepEntries()) returnUrl = withParam(returnUrl, encodeURIComponent(name), value)
    return withParam(returnUrl, "checkout_attempt_key", this.attemptKey)
  }

  // The downgrade impact card's keep[<feature>] steppers render outside the
  // form and join it by its id, so the form's own data holds them.
  keepEntries() {
    const form = this.element.closest("form")
    if (!form) return []
    return [...new FormData(form).entries()].filter(([name]) => name.startsWith("keep["))
  }

  billingUrl() {
    return this.returnUrlValue.split("?")[0].replace("/confirm", "")
  }

  showError(message) {
    if (this.hasErrorsTarget) {
      this.errorsTarget.textContent = message
    }
  }

  clearErrors() {
    if (this.hasErrorsTarget) {
      this.errorsTarget.textContent = ""
    }
  }

  setLoading(loading) {
    this.submitButtonTarget.disabled = loading
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("d-none", !loading)
    }
  }
}
